import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { Observable, filter, map, tap } from 'rxjs';
import { selectUserProfile } from '../store/auth.selectors';
import { UserProfile } from '../utility/auth.models';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  constructor(private readonly store: Store, private readonly snackBar: MatSnackBar) {}

  private normalizePermissions(permissions: string | string[]): string[] {
    if (Array.isArray(permissions))
      return permissions
        .map(permission => permission?.trim())
        .filter((permission): permission is string => !!permission);

    const normalized = permissions?.trim();
    return normalized ? [normalized] : [];
  }

  public hasPermissions(permissions: string | string[], showMessage = false): Observable<boolean> {
    return this.store.select(selectUserProfile).pipe(
      filter(user => !!user && Array.isArray(user.permissions)),
      map((user: UserProfile | null | undefined) => {
        const normalizedPermissions = this.normalizePermissions(permissions);
        if (normalizedPermissions.length === 0) return true;

        return normalizedPermissions.every(permission => user!.permissions?.includes(permission));
      }),
      tap((hasPermission: boolean) => {
        if (!hasPermission && showMessage)
          this.snackBar.open('You do not have permission to perform this action', 'Dismiss');
      })
    );
  }

  public hasSomePermission(
    permissions: string | string[],
    showMessage = false
  ): Observable<boolean> {
    return this.store.select(selectUserProfile).pipe(
      map(user => {
        if (!user) return false;
        const normalizedPermissions = this.normalizePermissions(permissions);
        if (normalizedPermissions.length === 0) return true;

        return normalizedPermissions.some(permission => user.permissions?.includes(permission));
      }),
      tap((hasPermission: boolean) => {
        if (!hasPermission && showMessage)
          this.snackBar.open('You do not have permission to perform this action', 'Dismiss');
      })
    );
  }
}
